function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Log from '@deephaven/log';
import { EMPTY_FUNCTION, TimeUtils } from '@deephaven/utils';
import MaskedInput from "./MaskedInput.js";
import { DEFAULT_GET_PREFERRED_REPLACEMENT_STRING } from "./MaskedInputUtils.js";
import { jsx as _jsx } from "react/jsx-runtime";
var log = Log.module('TimeInput');
var TIME_PATTERN = '([01][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]';
var EXAMPLES = ['00:00:00', '12:34:56', '23:59:59'];
function fixIncompleteValue(value) {
  // If value is not a complete HH:mm:ss time, fill missing parts with 0
  if (value != null) {
    return "".concat(value.substring(0, 8).replace(/\u2007/g, '0')).concat("00:00:00".substring(value.length));
  }
  return value;
}

// Forward ref causes a false positive for display-name in eslint:
// https://github.com/yannickcr/eslint-plugin-react/issues/2269
// eslint-disable-next-line react/display-name
var TimeInput = /*#__PURE__*/React.forwardRef((props, ref) => {
  var _props$allowValueWrap = props.allowValueWrapping,
    allowValueWrapping = _props$allowValueWrap === void 0 ? true : _props$allowValueWrap,
    _props$className = props.className,
    className = _props$className === void 0 ? '' : _props$className,
    _props$onChange = props.onChange,
    onChange = _props$onChange === void 0 ? EMPTY_FUNCTION : _props$onChange,
    _props$value = props.value,
    propsValue = _props$value === void 0 ? 0 : _props$value,
    _props$onFocus = props.onFocus,
    onFocus = _props$onFocus === void 0 ? EMPTY_FUNCTION : _props$onFocus,
    _props$onBlur = props.onBlur,
    onBlur = _props$onBlur === void 0 ? EMPTY_FUNCTION : _props$onBlur,
    _props$onSelect = props.onSelect,
    onSelect = _props$onSelect === void 0 ? EMPTY_FUNCTION : _props$onSelect,
    dataTestId = props['data-testid'];
  var _useState = useState(TimeUtils.formatTime(propsValue)),
    _useState2 = _slicedToArray(_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];
  var parsedValueRef = useRef(propsValue);
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    selection = _useState4[0],
    _setSelection = _useState4[1];
  var inputRef = useRef(null);
  useImperativeHandle(ref, () => ({
    focus: () => {
      var _inputRef$current;
      (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.focus();
    },
    setSelection: newSelection => {
      var _inputRef$current2;
      (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 || _inputRef$current2.focus();
      _setSelection(newSelection);
    }
  }), []);
  useEffect(function setFormattedTime() {
    // Ignore value prop update if it matches the displayed value
    // to preserve the displayed value while typing
    if (parsedValueRef.current !== propsValue) {
      setValue(TimeUtils.formatTime(propsValue));
      parsedValueRef.current = propsValue;
    }
  }, [parsedValueRef, propsValue]);
  function getNextSegmentValue(range, delta, segmentValue) {
    // Delta is backward because negative Y is up
    var maxValue = range.selectionStart === 0 ? 24 : 60;
    var newSegmentValue = parseInt(segmentValue, 10) - delta;
    if (Number.isNaN(newSegmentValue)) {
      newSegmentValue = 0;
    } else if (allowValueWrapping) {
      // Add max value and re-mod so we don't get negative values after mod
      newSegmentValue = (newSegmentValue % maxValue + maxValue) % maxValue;
    } else {
      newSegmentValue = Math.min(Math.max(0, newSegmentValue), maxValue - 1);
    }
    return "".concat(newSegmentValue).padStart(2, '0');
  }
  function getPreferredReplacementString(replaceValue, replaceIndex, newChar, selectionStart, selectionEnd) {
    if (selectionStart === 0 && selectionEnd === 2 && replaceIndex === 1 && parseInt(newChar, 10) > 1) {
      // DH-10082 Special case for when typing `3` when it's already 12
      return "0".concat(newChar).concat(replaceValue.substring(2));
    }
    return DEFAULT_GET_PREFERRED_REPLACEMENT_STRING(replaceValue, replaceIndex, newChar);
  }
  var handleChange = useCallback(newValue => {
    log.debug('handleChange', newValue);
    setValue(newValue);
    parsedValueRef.current = TimeUtils.parseTime(fixIncompleteValue(newValue));
    onChange(parsedValueRef.current);
  }, [onChange]);
  var handleBlur = useCallback(() => {
    var fixedValue = fixIncompleteValue(value);
    // Update the value displayed in the input
    // onChange with the fixed value already triggered in handleChange
    if (fixedValue !== value) {
      setValue(fixedValue);
    }
    onBlur();
  }, [value, onBlur]);
  var handleSelect = useCallback(newSelection => {
    _setSelection(newSelection);
    onSelect(newSelection);
  }, [onSelect]);
  return /*#__PURE__*/_jsx(MaskedInput, {
    ref: inputRef,
    className: className,
    example: EXAMPLES,
    getNextSegmentValue: getNextSegmentValue,
    getPreferredReplacementString: getPreferredReplacementString,
    onChange: handleChange,
    onSelect: handleSelect,
    pattern: TIME_PATTERN,
    selection: selection,
    value: value,
    onFocus: onFocus,
    onBlur: handleBlur,
    "data-testid": dataTestId
  });
});
export default TimeInput;
//# sourceMappingURL=TimeInput.js.map